import { Controller } from "@hotwired/stimulus"

// Card #4690: Project vs Cards view toggle for the Program page's Sprint
// Board section. Both views are rendered server-side from the same
// Programs::SprintBoard data (one projectPanel + one cardsPanel per sprint
// week column), so this is a pure client-side visibility swap -- no fetch,
// no reload, and it doesn't disturb whichever sprint-column-pagination
// instance is running inside the panel that's currently showing. Project is
// the default view (both via the server-rendered classes and this
// controller's own default below).
//
// Card #4707: the Assignee filter form submits a Turbo Frame GET request
// that replaces this whole frame (including this controller's own element),
// which used to silently reset the toggle back to Project every time --
// there was nowhere the chosen view was recorded. Fixed by writing the
// current view into the `viewField` hidden input (same pattern as the
// existing `show_completed` hidden field), which rides along in the
// Assignee form's query string as `view=project`/`view=cards`; the ERB
// partial reads `params[:view]` back out to pick which panel/button classes
// render on the next response. `apply()` also keeps the Copy-for-AI hidden
// clipboard source in sync with whichever panel is currently visible (card
// #4707 part 2) -- `connect()` seeds it once from the server-rendered state
// so a page load with ?view=cards already has the right text staged before
// any click happens.
export default class extends Controller {
  static targets = [
    "projectBtn", "cardsBtn", "projectPanel", "cardsPanel",
    "viewField", "copySource", "projectCopyText", "cardsCopyText"
  ]

  connect() {
    this.syncCopySource(this.showingCards)
  }

  get showingCards() {
    return this.hasViewFieldTarget && this.viewFieldTarget.value === "cards"
  }

  showProject() {
    this.apply(false)
  }

  showCards() {
    this.apply(true)
  }

  apply(showingCards) {
    this.projectPanelTargets.forEach((panel) => panel.classList.toggle("hidden", showingCards))
    this.cardsPanelTargets.forEach((panel) => panel.classList.toggle("hidden", !showingCards))

    if (this.hasProjectBtnTarget) {
      this.projectBtnTarget.classList.toggle("bg-white", !showingCards)
      this.projectBtnTarget.classList.toggle("shadow-sm", !showingCards)
      this.projectBtnTarget.classList.toggle("text-gray-900", !showingCards)
      this.projectBtnTarget.classList.toggle("text-gray-500", showingCards)
    }

    if (this.hasCardsBtnTarget) {
      this.cardsBtnTarget.classList.toggle("bg-white", showingCards)
      this.cardsBtnTarget.classList.toggle("shadow-sm", showingCards)
      this.cardsBtnTarget.classList.toggle("text-gray-900", showingCards)
      this.cardsBtnTarget.classList.toggle("text-gray-500", !showingCards)
    }

    if (this.hasViewFieldTarget) {
      this.viewFieldTarget.value = showingCards ? "cards" : "project"
    }

    this.syncCopySource(showingCards)
  }

  // Card #4707: keeps the Copy-for-AI hidden clipboard source (data-clipboard-target="source")
  // matching whatever's actually on screen -- the project-grouped text when
  // the Project panel is showing, the flat PLANNED/UNPLANNED text when Cards
  // is showing. Both texts are pre-rendered server-side (see
  // programs/_sprint_board.html.erb's hidden textareas) -- textarea.value is
  // used rather than reading rendered markup so that, when the content
  // includes `&`/`<`/etc., the browser's own HTML-entity decoding from
  // parsing the textarea does the unescaping for us.
  syncCopySource(showingCards) {
    if (!this.hasCopySourceTarget) return

    if (showingCards && this.hasCardsCopyTextTarget) {
      this.copySourceTarget.textContent = this.cardsCopyTextTarget.value
    } else if (!showingCards && this.hasProjectCopyTextTarget) {
      this.copySourceTarget.textContent = this.projectCopyTextTarget.value
    }
  }
}
